import React, { useCallback, useEffect, useState } from 'react'
import { Netmask } from 'netmask'
import Router from 'next/router'
import Link from 'next/link'
import toast from 'react-hot-toast'
import { useForm } from 'react-hook-form'
import { Feature, Tier } from '@prisma/client'
import { useSession } from 'next-auth/react'
import {
  ConfigWithFences, getUpdatableConfigFields, updateConfig, useTenantFeatures
} from '@src/lib/apiClient'
import { removeEmptyFields } from 'lib/api/normalizeFields'
import { useGeolocated } from 'react-geolocated'

type ConfigFormProps = {
  config?: ConfigWithFences;
  mutate?: () => void;
}

export const DEFAULT_INSTRUCTIONS = 'Select an exhibition below to view synchronized captions.'

export const ConfigForm: React.FC<ConfigFormProps> = ({
  config, mutate,
}: ConfigFormProps) => {
  const { data: session } = useSession()
  const { data: features } = useTenantFeatures()
  const [fencing, setFencing] = useState(
    // eslint-disable-next-line no-nested-ternary
    config.geoFence
      ? 'geoFence'
      : config.netmaskFence ? 'netmaskFence' : ''
  )

  const {
    register, handleSubmit, formState: { isValid, isSubmitting, errors }, setValue,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      ...getUpdatableConfigFields(config),
    },
  })

  useEffect(() => {
    if (fencing !== 'geoFence') {
      // Clear when geoFence when switching away
      setValue('geoFence', null)
      if (fencing === 'netmaskFence') {
        // Init netmask fence when switching to
        setValue('netmaskFence', config?.netmaskFence ?? {
          message: undefined,
          mask: undefined,
        })
      }
    }
    if (fencing !== 'netmaskFence') {
      // clear netmask fence when switching away
      setValue('netmaskFence', null)
      if (fencing === 'geoFence') {
        // init geofence when switching to
        setValue('geoFence', config?.geoFence ?? {
          lat: undefined,
          lon: undefined,
          radius: undefined,
        })
      }
    }
  }, [fencing, setValue, register, config?.geoFence, config?.netmaskFence])

  const submitData = async (newConfig: ConfigWithFences) => {
    const normalizedConfig = removeEmptyFields(newConfig, config)
    const { error } = await updateConfig(normalizedConfig, config)
    if (error) {
      toast.error(error.message)
    } else {
      mutate()
      toast.success('Config updated.')
      await Router.push('/admin')
    }
  }

  const [mountGeoLocation, setMountGeoLocation] = useState(false)

  const triggerMountGeoLocation = useCallback((e) => {
    e.preventDefault()
    setMountGeoLocation(true)
  }, [setMountGeoLocation])

  return (
    <div>
      <form onSubmit={handleSubmit(submitData)}>
        <div className="form-header">
          <div className="form-header__left">
            <h1>
              Global Config
            </h1>
          </div>
          <a href="https://docs.accesskit.cc/reference/global-config" target="_blank" rel="noreferrer">Help</a>
        </div>
        <label htmlFor="organizationName">
          Organization Name
          <input
            placeholder="Acme Museum of Art"
            type="text"
            {...register('organizationName')}
          />
        </label>
        <label htmlFor="indexBacklinkText">
          Home Back Link Text
          <input placeholder="Mobile Guide" type="text" {...register('indexBacklinkText')} />
        </label>
        <label htmlFor="indexBacklinkUrl">
          Home Back Link URL
          <input
            placeholder="https://acme.org/mobile-guide"
            type="text"
            {...register('indexBacklinkUrl')}
          />
        </label>
        <label htmlFor="title">
          Title
          <input placeholder="Captions and Audio" type="text" {...register('title')} />
        </label>
        <label htmlFor="instructions">
          Instructions
          <input
            placeholder={DEFAULT_INSTRUCTIONS}
            type="text"
            {...register('instructions')}
          />
        </label>
        <label htmlFor="fencing" className="fencing-label">
          Select a spatial restriction mode to prevent accessing content outside of the museum.
          <select value={fencing} onChange={(e) => setFencing(e.target.value)}>
            <option value="">None</option>
            <option value="geoFence">Geofencing (uses location services)</option>
            <option value="netmaskFence">Netmask Fencing (requires joining a specific Wifi Network)</option>
          </select>
        </label>
        {fencing === 'netmaskFence' && (
          <>
            <label htmlFor="netmaskFence.mask">
              Netmask
              <input
                type="text"
                placeholder="127.0.0.1"
                {...register('netmaskFence.mask', {
                  shouldUnregister: true,
                  validate: (v: string) => { try { Netmask(v); return true } catch { return 'Invalid netmask' } },
                  required: true,
                })}
              />
              <p>{errors.netmaskFence?.mask?.message}</p>
              <p>{errors.netmaskFence?.mask?.type === 'required' && 'Netmask is required.'}</p>
            </label>

            <label htmlFor="netmaskFence.message">
              Let users know how to join the required network:
              <input
                type="text"
                placeholder="Join the free public WiFi network 'your-museums-wifi' to access."
                {...register('netmaskFence.message', {
                  shouldUnregister: true,
                  required: true,
                })}
              />
              <p>{typeof errors.netmaskFence?.message === 'string' ? errors.netmaskFence?.message : errors.netmaskFence?.message?.message}</p>
              <p>{typeof errors.netmaskFence?.message !== 'string' && errors.netmaskFence?.message?.type === 'required' && 'Netmask is required.'}</p>
            </label>
          </>
        )}

        {fencing === 'geoFence' && (
          <>
            <button className="config-form-button" type="button" onClick={triggerMountGeoLocation}>Use current location's latitude and longitude</button>
            {mountGeoLocation && <SetGeoLocationCoordinates setValue={setValue} />}
            <label htmlFor="geoFence.lat">
              GeoFencing Latitude
              <input
                type="number"
                step="0.0001"
                placeholder="40.7128"
                {...register('geoFence.lat', {
                  shouldUnregister: true,
                  required: true,
                  // valueAsNumber: true,
                  min: -90,
                  max: 90,
                })}
              />
              <p>{errors.geoFence?.lat?.message}</p>
              <p>{errors.geoFence?.lat?.type === 'required' && 'Latitude is required.'}</p>
              <p>{['min', 'max'].includes(errors.geoFence?.lat?.type) && 'Latitude must be between -90deg and +90.'}</p>
            </label>
            <label htmlFor="geoFence.lon">
              GeoFencing Longitude
              <input
                type="number"
                step="0.0001"
                placeholder="-71.1212"
                {...register('geoFence.lon', {
                  shouldUnregister: true,
                  required: true,
                  // valueAsNumber: true,
                  min: -180,
                  max: 180,
                })}
              />
              <p>{errors.geoFence?.lon?.message}</p>
              <p>{errors.geoFence?.lon?.type === 'required' && 'Longitude is required.'}</p>
              <p>{['min', 'max'].includes(errors.geoFence?.lon?.type) && 'Longitude must be between -180deg and +180.'}</p>
            </label>
            <label htmlFor="geoFence.radius">
              GeoFencing Radius (miles)
              <input
                type="number"
                step="0.1"
                placeholder="0.5"
                {...register('geoFence.radius', {
                  shouldUnregister: true,
                  required: true,
                  // valueAsNumber: true,
                  min: 0.1,
                  max: 2,
                })}
              />
              <p>{errors.geoFence?.radius?.message}</p>
              <p>{errors.geoFence?.radius?.type === 'required' && 'Radius is required.'}</p>
              <p>{['min', 'max'].includes(errors.geoFence?.radius?.type) && 'Radius must be between 0.1mi and 2mi.'}</p>
            </label>
          </>
        )}

        <label htmlFor="googleAnalyticsId">
          Google Analytics Tracking ID
          <input placeholder="GA-XXXXXXX" type="text" {...register('googleAnalyticsId')} />
        </label>
        <label htmlFor="guestStylesheetUrl">
          Guest Stylesheet URL
          {' '}
          {!features?.includes(Feature.CUSTOM_STYLING) && <em> Not included in current plan.</em>}
          <input
            disabled={!features?.includes(Feature.CUSTOM_STYLING)}
            placeholder="/styles/guest-default.css"
            type="text"
            {...register('guestStylesheetUrl')}
          />
        </label>
        {features?.includes(Feature.CUSTOM_STYLING) ? (
          <>
            <label htmlFor="faviconUrl">
              Favicon URL
              <input
                placeholder="/images/favicon-32x32.png"
                type="text"
                {...register('faviconUrl')}
              />
            </label>
            <label htmlFor="leftArrowSvgUrl">
              Custom Left Arrow SVG URL
              <input
                placeholder="/images/left_arrow.svg"
                type="text"
                {...register('leftArrowSvgUrl')}
              />
            </label>
            <label htmlFor="downArrowSvgUrl">
              Custom Down Arrow SVG URL
              <input
                placeholder="/images/down_arrow.svg"
                type="text"
                {...register('downArrowSvgUrl')}
              />
            </label>
            <label htmlFor="audioOnSvgUrl">
              Custom Audio On SVG URL
              <input
                placeholder="/images/audio_on.svg"
                type="text"
                {...register('audioOnSvgUrl')}
              />
            </label>
            <label htmlFor="audioOffSvgUrl">
              Custom Audio Off SVG URL
              <input
                placeholder="/images/audio_off.svg"
                type="text"
                {...register('audioOffSvgUrl')}
              />
            </label>
            <label htmlFor="audioDescriptionSvgUrl">
              Custom Audio Description SVG URL
              <input
                placeholder="/images/audio_description.svg"
                type="text"
                {...register('audioDescriptionSvgUrl')}
              />
            </label>
            <label htmlFor="closedCaptionsSvgUrl">
              Custom Closed Captions SVG URL
              <input
                placeholder="/images/closed_captions.svg"
                type="text"
                {...register('closedCaptionsSvgUrl')}
              />
            </label>
            <label htmlFor="closedCaptionsSvgUrl">
              Custom Loader SVG URL
              <input
                placeholder="/images/loader.svg"
                type="text"
                {...register('loaderSvgUrl')}
              />
            </label>
          </>
        )
          : <p>Custom styling and icons not included in current plan.</p>}
        <br />
        <div className="form-submit">
          <input type="submit" value="Update Config" disabled={!isValid || isSubmitting} />
          <Link href="/admin">
            or Cancel
          </Link>
          {session?.user?.tenantData?.tenantTier && session.user.tenantData.tenantTier !== Tier.COMMUNITY && (
            <Link href="/admin/invite" className="form-submit__manage-users">
              <input type="button" value="Manage Users" />
            </Link>
          )}
        </div>
      </form>
    </div>
  )
}

const SetGeoLocationCoordinates: React.FC<{ setValue: (field: string, data: any) => void }> = ({ setValue }) => {
  const { coords } = useGeolocated({
    positionOptions: {
      enableHighAccuracy: false,
    },
    userDecisionTimeout: 5000,
  })
  useEffect(() => {
    if (coords) {
      setValue('geoFence.lat', Math.round(10000 * coords.latitude) / 10000)
      setValue('geoFence.lon', Math.round(10000 * coords.longitude) / 10000)
      setValue('geoFence.radius', 0.5)
    }
  }, [setValue, coords])
  return (
    <></>
  )
}
